import React from 'react';

export default function CareerInsights({ navigate }) {
  return (
    <main>
      {/* Top Hero Section */}
      <section className="page-hero">
        <div className="container">
          <span className="eyebrow">Corporate Excel Training</span>
          <h1>Excel Corporate Training Programs</h1>
          <p className="lead max-820">
            Practical, department-focused programs designed to strengthen Excel capability, automate repetitive work and improve business reporting.
          </p>
        </div>
      </section>

      {/* Available Programs (Accordion details) - Swapped to 1st */}
      <section className="corporate-programmes-section" aria-labelledby="corporate-programmes-heading">
        <div className="container corporate-programmes-wrap">
          <div className="corporate-programmes-heading">
            <span className="eyebrow">Available Programs</span>
            <h2 id="corporate-programmes-heading">Choose the right program for your team.</h2>
          </div>

          <div className="corporate-programme-list">
            {/* 1st: Excel DNA Program */}
            <details className="corporate-programme">
              <summary>
                <span className="corporate-programme-number">01</span>
                <span className="corporate-programme-title">
                  <strong>Excel <span className="highlight-red">DNA</span> Program</strong>
                  <small>Basic to Intermediate Corporate Programme</small>
                </span>
                <span className="corporate-programme-plus" aria-hidden="true"></span>
              </summary>
              <div className="corporate-programme-panel">
                <p className="corporate-programme-lead">Build a consistent Excel foundation across the organisation by covering the complete core toolkit employees need for accurate, efficient and confident everyday work.</p>

                <div className="corporate-programme-content">
                  <div>
                    <h3>What Employees Will Develop</h3>
                    <ul className="check-list">
                      <li>A strong understanding of Excel Toolkit and practical capabilities</li>
                      <li>Structured thinking for organizing data, reports and everyday workflows</li>
                      <li>Logical approaches to calculations, conditions and business analysis</li>
                      <li>Problem-solving strategies for handling workplace Excel challenges</li>
                      <li>Confidence in selecting the right functions and tools for each task</li>
                      <li>Practical skills in data cleaning, transformation, PivotTables and visual reporting</li>
                      <li>Faster and more efficient working methods using shortcuts and smart techniques</li>
                      <li>A systematic approach to building accurate, reliable and reusable Excel outputs</li>
                    </ul>
                  </div>
                  <aside className="corporate-outcome-box">
                    <span>End-of-day capability</span>
                    <p>Employees develop a reliable Excel foundation, understand the purpose of the main workplace tools and become more accurate, independent and productive in their daily responsibilities.</p>
                    <small>Best suited for beginner and intermediate users, mixed-skill teams and organisations seeking a common Excel standard.</small>
                  </aside>
                </div>

                <a
                  className="btn btn-primary"
                  href="#/contact"
                  onClick={(e) => {
                    e.preventDefault();
                    localStorage.setItem('selectedProgram', 'Excel DNA Program');
                    navigate('contact');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Inquire About This Programme
                </a>
              </div>
            </details>

            {/* 2nd: Excel Power Mastery Program */}
            <details className="corporate-programme">
              <summary>
                <span className="corporate-programme-number">02</span>
                <span className="corporate-programme-title">
                  <strong>Excel <span className="highlight-red">Power Mastery</span> Program</strong>
                  <small>Intermediate to Expert Corporate Programme</small>
                </span>
                <span className="corporate-programme-plus" aria-hidden="true"></span>
              </summary>
              <div className="corporate-programme-panel">
                <p className="corporate-programme-lead">Develop advanced Excel professionals who can solve complex workplace problems, design systematic solutions and use modern Excel power tools to improve business performance.</p>

                <div className="corporate-programme-content">
                  <div>
                    <h3>What Employees Will Master</h3>
                    <ul className="check-list">
                      <li>Advanced analytical thinking for complex business and operational challenges</li>
                      <li>Advanced formulas, lookup methods and dynamic calculation models</li>
                      <li>Systematic methods for designing scalable and maintainable Excel solutions</li>
                      <li>Efficient data preparation, consolidation and transformation using advanced tools</li>
                      <li>Advanced data modelling, analysis and performance evaluation</li>
                      <li>Interactive dashboards and management reports for better decision-making</li>
                      <li>Automation of recurring processes using macros, VBA and AI-powered tools</li>
                      <li>Stronger controls, error handling and documentation for reliable business solutions</li>
                    </ul>
                  </div>
                  <aside className="corporate-outcome-box">
                    <span>End-of-day capability</span>
                    <p>Employees become capable of designing advanced Excel solutions, automating complex processes and transforming business data into reliable insights for operational and management decisions.</p>
                    <small>Best suited for experienced Excel users, analysts, managers, reporting specialists and internal Excel champions.</small>
                  </aside>
                </div>

                <a
                  className="btn btn-primary"
                  href="#/contact"
                  onClick={(e) => {
                    e.preventDefault();
                    localStorage.setItem('selectedProgram', 'Excel Power Mastery Program');
                    navigate('contact');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Inquire About This Programme
                </a>
              </div>
            </details>

            {/* 3rd: Excel Dashboard Mastery */}
            <details className="corporate-programme">
              <summary>
                <span className="corporate-programme-number">03</span>
                <span className="corporate-programme-title">
                  <strong>Excel Dashboard Mastery</strong>
                  <small>1-Day Practical Workshop</small>
                </span>
                <span className="corporate-programme-plus" aria-hidden="true"></span>
              </summary>
              <div className="corporate-programme-panel">
                <p className="corporate-programme-lead">Turn raw data into an interactive Excel dashboard that clearly tracks KPIs, responsibilities and operational progress.</p>

                <div className="corporate-programme-content">
                  <div>
                    <h3>What employees will learn</h3>
                    <ul className="check-list">
                      <li>Prepare clean, dashboard-ready data structures</li>
                      <li>Import, clean and combine data using Power Query</li>
                      <li>Build efficient data models using Power Pivot</li>
                      <li>Create PivotTables, PivotCharts, slicers and timelines</li>
                      <li>Design KPI cards, visual alerts and interactive controls</li>
                      <li>Apply advanced dashboard layouts and customisation</li>
                      <li>Create a refreshable reporting workflow</li>
                    </ul>
                  </div>
                  <aside className="corporate-outcome-box">
                    <span>End-of-day capability</span>
                    <p>Each employee will be able to build an interactive Excel dashboard for monitoring personal or departmental KPIs, tracking job tasks and visualising progress against day-to-day responsibilities.</p>
                    <small>Best suited for analysts, executives, team leaders and professionals responsible for performance reporting.</small>
                  </aside>
                </div>

                <a
                  className="btn btn-primary"
                  href="#/contact"
                  onClick={(e) => {
                    e.preventDefault();
                    localStorage.setItem('selectedProgram', 'Excel Dashboard Mastery');
                    navigate('contact');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Inquire About This Workshop
                </a>
              </div>
            </details>

            {/* 4th: Repetitive Task Automation */}
            <details className="corporate-programme">
              <summary>
                <span className="corporate-programme-number">04</span>
                <span className="corporate-programme-title">
                  <strong>Repetitive Task Automation: Excel + AI &amp; VBA</strong>
                  <small>1-Day Practical Workshop</small>
                </span>
                <span className="corporate-programme-plus" aria-hidden="true"></span>
              </summary>
              <div className="corporate-programme-panel">
                <p className="corporate-programme-lead">Help employees convert repetitive Excel work into faster, button-driven workflows using practical AI tools, macros and VBA—with no prior programming experience required.</p>

                <div className="corporate-programme-content">
                  <div>
                    <h3>What employees will learn</h3>
                    <ul className="check-list">
                      <li>Identify high-value tasks suitable for automation</li>
                      <li>Use leading AI tools to plan and build Excel solutions</li>
                      <li>Generate, understand and validate formulas with AI</li>
                      <li>Record and improve macros for repetitive processes</li>
                      <li>Use AI-assisted VBA to connect multiple workflow steps</li>
                      <li>Create buttons and simple user-controlled automations</li>
                      <li>Test, troubleshoot and safely maintain automated files</li>
                    </ul>
                  </div>
                  <aside className="corporate-outcome-box">
                    <span>End-of-day capability</span>
                    <p>Employees will be able to convert a repetitive Excel workflow into a simplified automation that can be operated through a few clear actions or buttons—saving hours of manual work and adding measurable value to the organisation.</p>
                    <small>Best suited for professionals who repeatedly prepare, clean, consolidate or distribute Excel reports.</small>
                  </aside>
                </div>

                <a
                  className="btn btn-primary"
                  href="#/contact"
                  onClick={(e) => {
                    e.preventDefault();
                    localStorage.setItem('selectedProgram', 'Repetitive Task Automation: Excel + AI & VBA');
                    navigate('contact');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Inquire About This Workshop
                </a>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* The Corporate Training Process - Swapped to 2nd */}
      <section className="corporate-process-section" aria-labelledby="corporate-process-heading">
        <div className="container">
          <div className="corporate-process-heading">
            <span className="eyebrow">Our Methodology</span>
            <h2 id="corporate-process-heading">Corporate Training Process</h2>
          </div>

          <div className="corporate-process-track">
            <article className="corporate-process-step">
              <div className="corporate-process-step-badge">01</div>
              <h3>Understand the Need</h3>
              <p>We identify your team’s responsibilities, Excel skill gaps, workflow challenges and expected business outcomes.</p>
            </article>

            <article className="corporate-process-step">
              <div className="corporate-process-step-badge">02</div>
              <h3>Design the Programme</h3>
              <p>The training content, exercises and datasets are tailored to departmental roles, KPIs and everyday reporting needs.</p>
            </article>

            <article className="corporate-process-step">
              <div className="corporate-process-step-badge">03</div>
              <h3>Deliver Practical Learning</h3>
              <p>Participants learn through guided demonstrations, real workplace scenarios and hands-on exercises, onsite or online.</p>
            </article>

            <article className="corporate-process-step">
              <div className="corporate-process-step-badge">04</div>
              <h3>Validate the Learning Outcomes</h3>
              <p>Practical activities and feedback are used to assess progress, clarify gaps and strengthen participants’ understanding.</p>
            </article>

            <article className="corporate-process-step">
              <div className="corporate-process-step-badge">05</div>
              <h3>Support Workplace Application</h3>
              <p>Participants receive relevant resources and follow-up guidance to apply better Excel methods in their daily work.</p>
            </article>
          </div>
        </div>
      </section>
    </main>
  );
}
